import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneTilt, MapPin, Phone, EnvelopeSimple, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const services = [
  "Income Tax Filing & Advisory",
  "GST Registrations & Returns",
  "Audit & Assurance",
  "Company Incorporation & ROC",
  "Accounting & Bookkeeping",
  "Financial & Regulatory Advisory",
];

const initial = { name: "", email: "", phone: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone and message.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", form);
      toast.success("Thank you. A partner will respond within one business day.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#F3F0EA]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="overline mb-5">/ Speak with the firm</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] text-[#1B3B2B]">
            Write to us. A partner replies within one business day.
          </h2>
          <p className="mt-6 text-[#4A5D53] leading-relaxed max-w-md">
            Tell us what you&apos;re working on — incorporation, tax notice, audit, or quiet
            ongoing compliance. We&apos;ll get back with a scope and a flat retainer.
          </p>

          <ul className="mt-10 space-y-6">
            <li className="flex gap-4" data-testid="contact-address">
              <MapPin size={22} weight="duotone" className="mt-1 text-[#1B3B2B]" />
              <div>
                <div className="overline text-[10px] mb-1">Office</div>
                <div className="text-[#1B3B2B] text-sm leading-relaxed">
                  #113, 2nd Floor, 100 ft Ring Road, <br />
                  5th Block, 3rd Phase, Banashankari 3rd Stage, <br />
                  Bengaluru, Karnataka 560085
                </div>
              </div>
            </li>
            <li className="flex gap-4" data-testid="contact-phone">
              <Phone size={22} weight="duotone" className="mt-1 text-[#1B3B2B]" />
              <div>
                <div className="overline text-[10px] mb-1">Telephone</div>
                <div className="text-[#1B3B2B] text-sm">+91 78294 01779</div>
              </div>
            </li>
            <li className="flex gap-4" data-testid="contact-email">
              <EnvelopeSimple size={22} weight="duotone" className="mt-1 text-[#1B3B2B]" />
              <div>
                <div className="overline text-[10px] mb-1">Email</div>
                <a
                  href="mailto:aditya@caadityakumar.com"
                  className="text-[#1B3B2B] text-sm hover:text-[#D4AF37] transition-colors"
                >
                  aditya@caadityakumar.com
                </a>
              </div>
            </li>
            <li className="flex gap-4" data-testid="contact-hours">
              <Clock size={22} weight="duotone" className="mt-1 text-[#1B3B2B]" />
              <div>
                <div className="overline text-[10px] mb-1">Office Hours</div>
                <div className="text-[#1B3B2B] text-sm">Mon–Sat · 10:00 – 19:00 IST</div>
              </div>
            </li>
          </ul>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7 bg-[#FDFBF7] border border-[#D1CEC5] p-8 md:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <label className="block">
              <span className="overline text-[10px] block mb-2">Full name</span>
              <input
                data-testid="contact-name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your name"
                className="input-editorial"
                required
              />
            </label>
            <label className="block">
              <span className="overline text-[10px] block mb-2">Email</span>
              <input
                data-testid="contact-email-input"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.in"
                className="input-editorial"
                required
              />
            </label>
            <label className="block">
              <span className="overline text-[10px] block mb-2">Phone</span>
              <input
                data-testid="contact-phone-input"
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="+91 ____ _____"
                className="input-editorial"
                required
              />
            </label>
            <label className="block">
              <span className="overline text-[10px] block mb-2">Service of interest</span>
              <select
                data-testid="contact-service"
                name="service"
                value={form.service}
                onChange={onChange}
                className="input-editorial"
              >
                <option value="">Select a service</option>
                {services.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
            </label>
          </div>

          <label className="block mt-5">
            <span className="overline text-[10px] block mb-2">Brief</span>
            <textarea
              data-testid="contact-message"
              name="message"
              value={form.message}
              onChange={onChange}
              rows={6}
              placeholder="A short note on your situation, deadlines and what success would look like."
              className="input-editorial resize-none"
              required
            />
          </label>

          <div className="mt-8 flex items-center justify-between gap-6">
            <p className="text-xs text-[#4A5D53] max-w-sm">
              Submissions are received in confidence and reviewed by a partner before any reply.
            </p>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send enquiry"}
              <PaperPlaneTilt size={16} weight="bold" />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
